import { useState, useEffect } from 'react'
import { PageHeader } from '@/components/common/page-header'
import { Card, CardContent } from '@/components/ui/card'
import { Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useApiKeys } from '@/hooks/useApiKeyQueries'
import { useRevokeApiKey } from '@/hooks/useApiKeyMutations'
import {
  CreateApiKeyDialog,
  ApiKeyTable,
  DeleteApiKeyDialog,
  RevokeApiKeyDialog,
} from '@/components/api-keys'
import { toast } from 'sonner'

export function ApiKeysListPage() {
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [revokeId, setRevokeId] = useState<string | null>(null)

  const { data: apiKeys, isLoading, error } = useApiKeys()
  const revokeApiKey = useRevokeApiKey()

  const handleRevoke = (id: string) => {
    setRevokeId(id)
  }

  const handleDelete = (id: string) => {
    setDeleteId(id)
  }

  const handleCloseDelete = () => {
    setDeleteId(null)
  }

  const handleCloseRevoke = () => {
    setRevokeId(null)
  }

  const deletingKey = apiKeys?.find((k) => k.id === deleteId)
  const revokingKey = apiKeys?.find((k) => k.id === revokeId)

  useEffect(() => {
    if (error) {
      toast.error('Failed to load API keys')
    }
  }, [error])

  return (
    <div className="space-y-6">
      <PageHeader
        title="API Keys"
        subtitle="Manage API keys for programmatic access to WiCloud"
        action={
          <Button onClick={() => setCreateDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Create API Key
          </Button>
        }
      />

      <Card>
        <CardContent className="p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <p className="text-muted-foreground">Loading API keys...</p>
            </div>
          ) : (
            <ApiKeyTable
              apiKeys={apiKeys ?? []}
              onRevoke={handleRevoke}
              onDelete={handleDelete}
            />
          )}
        </CardContent>
      </Card>

      <CreateApiKeyDialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} />

      {deletingKey && (
        <DeleteApiKeyDialog
          open={deleteId !== null}
          onClose={handleCloseDelete}
          apiKeyId={deleteId!}
          apiKeyName={deletingKey.name}
        />
      )}

      {revokingKey && (
        <RevokeApiKeyDialog
          open={revokeId !== null}
          onClose={handleCloseRevoke}
          apiKeyId={revokeId!}
          apiKeyName={revokingKey.name}
        />
      )}
    </div>
  )
}
